import type { StationTask, StationTaskStatus } from '@ordr-as/ordr-service-types/epos';
import type { ExpoDisplayItem, ExpoOrder } from './types';

// Board presentation only: the server (StationView) owns what's at the station, each task's
// "waiting for", and the item totals. All that's left here is the visual ranking — which is what
// lets an optimistic status patch re-rank the board for free (see ADR 0004).

/** Statuses that keep a task on the board. RECALLED came back from done and needs attention again. */
export function isActiveStatus(status: StationTaskStatus | undefined): boolean {
  return status === 'PENDING' || status === 'QUEUED' || status === 'ONGOING' || status === 'RECALLED';
}

/** RECALLED renders like ONGOING: it is back in the kitchen's hands. */
export function isOngoingStatus(status: StationTaskStatus | undefined): boolean {
  return status === 'ONGOING' || status === 'RECALLED';
}

function boardRank(status: StationTaskStatus | undefined): number {
  if (isOngoingStatus(status)) return 0;
  if (status === 'QUEUED') return 1;
  return 2;
}

function timestampMs(stamp: string | undefined): number {
  // Tasks without a timestamp sort last within their rank.
  return stamp ? new Date(stamp).getTime() : Number.MAX_SAFE_INTEGER;
}

/**
 * What a task is ordered by inside its rank. Ongoing tasks go by when they were started rather than
 * when they were placed, so hitting Start joins a card to the end of the ongoing group instead of
 * jumping it ahead of work already in hand. `updatedUtc` is our stand-in for a started-at stamp.
 */
function rankTimeMs(task: StationTask): number {
  return isOngoingStatus(task.status) ? timestampMs(task.updatedUtc ?? task.createdUtc) : timestampMs(task.createdUtc);
}

/** Board order: ongoing → queued → pending, oldest first within a rank. */
export function sortBoardTasks(tasks: StationTask[]): StationTask[] {
  return [...tasks].sort((a, b) => boardRank(a.status) - boardRank(b.status) || rankTimeMs(a) - rankTimeMs(b));
}

function hasOngoingChip(order: ExpoOrder): boolean {
  return order.displayItems.some((item) => item.chips.some((chip) => chip.state === 'ONGOING'));
}

/** An order nobody has touched yet: nothing done and nothing in hand at any station. */
function isNotStarted(order: ExpoOrder): boolean {
  return order.doneCount === 0 && !hasOngoingChip(order);
}

function expoRank(order: ExpoOrder): number {
  if (order.allDone) return 2;
  return isNotStarted(order) ? 1 : 0;
}

/** When the order was handed over: the last task bump is what set `updatedUtc`. */
function deliveredAtMs(order: ExpoOrder): number {
  const stamp = order.updatedUtc ?? order.createdUtc;
  return stamp ? new Date(stamp).getTime() : 0;
}

/**
 * Expo order: in-flight work on top (in progress, then not started, oldest first). All-done orders
 * are Delivered under the universal auto-deliver rule, so they sink to the bottom — newest handoff
 * nearest the live cards, older ones further down until the server's window drops them.
 */
export function sortExpoOrders(orders: ExpoOrder[]): ExpoOrder[] {
  return [...orders].sort((a, b) => {
    const rank = expoRank(a) - expoRank(b);
    if (rank !== 0) return rank;
    if (a.allDone) return deliveredAtMs(b) - deliveredAtMs(a);
    return timestampMs(a.createdUtc) - timestampMs(b.createdUtc);
  });
}

/**
 * The expo card's progress line. A step count only says something when a product actually passes
 * through more than one station — on a single-station flow "1 / 1 steps" is noise, so it is dropped.
 */
export function expoProgressLabel(order: ExpoOrder): string {
  const stage = isNotStarted(order) ? 'Not started' : 'Ongoing';
  const multiStep = order.displayItems.some((item) => item.chips.length > 1);
  return multiStep ? `${stage} · ${order.doneCount} / ${order.totalCount} steps` : stage;
}

function updatedAtMs(task: StationTask): number {
  const stamp = task.updatedUtc ?? task.createdUtc;
  return stamp ? new Date(stamp).getTime() : 0;
}

/** Done tasks at the station, newest first. The endpoint already caps this set. */
export function selectRecentlyDone(tasks: StationTask[]): StationTask[] {
  return tasks.filter((task) => task.status === 'DONE').sort((a, b) => updatedAtMs(b) - updatedAtMs(a));
}

export function taskSummaryLine(task: StationTask): string {
  return (task.items ?? [])
    .map((item) => {
      const qty = item.quantity ?? 1;
      const name = item.itemName ?? 'Unknown item';
      return qty > 1 ? `${qty}× ${name}` : name;
    })
    .join(', ');
}

export type StationOrderDetail = {
  bookingOrderId: number;
  createdUtc?: string;
  orderComment?: string;
  displayItems: ExpoDisplayItem[];
};

/**
 * The "Order detail (view)" for a tapped order, assembled from this station's tasks for that group:
 * its lines as seen here, with no cross-station progress chips (deferred, ADR 0004). Returns null
 * when there's nothing to show, so the renderer needs no further guarding.
 */
export function assembleStationOrderDetail(tasks: StationTask[]): StationOrderDetail | null {
  const bookingOrderId = tasks[0]?.bookingOrderId;
  if (tasks.length === 0 || bookingOrderId === undefined) return null;

  const displayItems: ExpoDisplayItem[] = tasks.flatMap((task) =>
    (task.items ?? []).map((item) => ({
      key: `${task.id}-${item.id}`,
      name: item.itemName ?? 'Unknown item',
      quantity: item.quantity ?? 1,
      modifiers: (item.modifiers ?? []).map((m) => m.itemName ?? '').filter(Boolean),
      comment: item.comment ?? undefined,
      chips: [],
    }))
  );

  const createdUtc = tasks
    .map((task) => task.createdUtc)
    .filter((s): s is string => Boolean(s))
    .sort()[0];
  const orderComment = tasks.find((task) => task.orderComment)?.orderComment ?? undefined;

  return { bookingOrderId, createdUtc, orderComment, displayItems };
}
